/**
 * MFA step-up prompt shown when the API answers `step_up_required` to an approve / bulk approve / rollback.
 * The code goes to the identity step-up endpoint; the original action is retried on success.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Button } from '@qm/ui';
import { useApp } from '../../../../../lib/app-context';

export function StepUpPrompt({ action, busy, error, onSubmit, onCancel }: {
  action: 'approve' | 'rollback' | 'bulk'; busy: boolean; error: string | null; onSubmit: (code: string) => void; onCancel: () => void;
}) {
  const { t } = useApp();
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  useEffect(() => { input.current?.focus(); }, []);
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(code); };
  return (
    <form className="ar-stepup" role="group" aria-labelledby={`${id}-t`} onSubmit={submit} data-testid="step-up">
      <p className="ar-stepup__title" id={`${id}-t`}>{t('audit-remediation.actions.stepUp.title')}</p>
      <p className="ar-muted">{t(`audit-remediation.actions.stepUp.why.${action}`)}</p>
      <div className="cu-field">
        <label htmlFor={`${id}-c`}>{t('audit-remediation.actions.stepUp.code')}</label>
        <input ref={input} id={`${id}-c`} name="one-time-code" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} dir="ltr"
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-e` : undefined} />
      </div>
      {error ? <p className="qm-error" role="alert" id={`${id}-e`}>{t(error)}</p> : null}
      <div className="cu-btnrow">
        <Button type="submit" variant="accent" disabled={busy}>{t('audit-remediation.actions.stepUp.confirm')}</Button>
        <Button onClick={onCancel} disabled={busy}>{t('audit-remediation.actions.cancel')}</Button>
      </div>
    </form>
  );
}
